import { clone, pick } from '@/common/js/just.js'
import { queryImageDetailByIds } from '@/api/common.model.js'
import { getCarouselsById } from '@/api/toutiao.local.model.js'
import { dataFormatter } from '@/components/material-selector/utils.js'
import { MATERIAL_TYPE, MATERIAL_TYPE_ID } from '@/components/material-selector/consts.js'
import { getCreativeImageMode } from '@/views/create/bytedance/hooks/utils/plan.js'
import { getSubmitTargetingParams } from '@/views/create/bytedance/hooks/utils/index.js'
import { getInitForm } from '@/views/promote/targeting/components/toutiao/useToutiaoTargetingForm.js'
import {
  AUDIENCE_TYPE,
  DISTRICT_TYPE,
  INTEREST_ACTION_TYPES,
  PLATFORM as TARGETING_PLATFORM,
  REGION_TYPE,
  TOUTIAO_AD_MARKETING_SCENE
} from '@/views/promote/targeting/model/toutiao_targeting_consts.js'
import {
  BID_VALUE_TYPE,
  BUDGET_MODE,
  DEFAULT_SCHEDULE_TIME,
  DELIVERY_TYPE,
  MATERIAL_STYLE,
  PROJECT_AUDIENCE_TYPE,
  SCHEDULE_TIME_TYPE,
  SCHEDULE_TYPE
} from '@/views/create/bytedance/consts/index.js'
import {
  getConfigFormModel,
  getCreativeFormModel,
  getPlanFormModel,
  getProjectFormModel
} from '@/views/create/bytedance/model/form.js'

const TARGETING_MANAGED_KEYS = [
  'ac',
  'age',
  'gender',
  'inventory_type',
  'inventory_catalog',
  'audiences',
  'retargeting_tags_include',
  'retargeting_tags_exclude',
  'city',
  'district',
  'location_type',
  'interest_action_mode',
  'action_days',
  'action_scene',
  'action_categories',
  'action_words',
  'interest_categories',
  'interest_words',
  'platform',
  'android_osv',
  'ios_osv',
  'hide_if_converted',
  'hide_if_exists',
  'converted_time_duration',
  'device_type',
  'auto_extend_enabled',
  'auto_extend_targets',
  'smart_extend',
  'superior_popularity_type',
  'carrier',
  'activate_type',
  'device_brand',
  'launch_price'
]

const MATERIAL_STATUS_KEYS = ['material_status_frist', 'material_status_second', 'material_opt_status']
const MATERIAL_LIST_KEYS = [
  'video_material_list',
  'image_material_list',
  'carousel_material_list',
  'title_material_list'
]

function hasOwn(obj, key) {
  return Object.prototype.hasOwnProperty.call(obj, key)
}

function withoutUndefined(obj) {
  return Object.entries(obj).reduce((result, [key, value]) => {
    if (typeof value !== 'undefined') result[key] = value
    return result
  }, {})
}

function toEditableNumber(value) {
  if (value === '' || value === null || typeof value === 'undefined') return undefined
  const number = Number(value)
  return Number.isNaN(number) ? value : number
}

function getMaterialViewId(type, raw, index) {
  const materialId = raw.material_id
  return `std-${type}-${typeof materialId === 'undefined' ? index : String(materialId)}`
}

function makeFallbackMaterial(type, raw, index) {
  const materialId = raw.material_id
  return {
    id: getMaterialViewId(type, raw, index),
    type,
    name: `媒体已有素材（${String(materialId || raw.carousel_id || index + 1)}）`,
    preview_url: '',
    width: 0,
    height: 0,
    _stdExisting: true,
    _stdRaw: clone(raw)
  }
}

function attachExistingMaterial(view, type, raw, index) {
  return {
    ...view,
    id: getMaterialViewId(type, raw, index),
    type,
    material_id: raw.material_id,
    _stdExisting: true,
    _stdRaw: clone(raw)
  }
}

async function queryInternalMaterialMap(materialInfo) {
  const ids = [
    ...(materialInfo.image_material_list || []),
    ...(materialInfo.video_material_list || [])
  ]
    .map((item) => item.inner_id)
    .filter((id) => id !== null && typeof id !== 'undefined' && id !== '')

  if (!ids.length) return new Map()

  try {
    const details = (await queryImageDetailByIds({ imageDetailIds: [...new Set(ids)] })) || []
    return new Map(
      details
        .map((detail) => detail?.items?.[0])
        .filter(Boolean)
        .map((item) => [String(item.id), item])
    )
  } catch (e) {
    return new Map()
  }
}

async function queryCarouselMap(materialInfo, accountId) {
  const ids = (materialInfo.carousel_material_list || []).map((item) => item.carousel_id).filter(Boolean)
  if (!ids.length) return new Map()

  try {
    const carousels = (await getCarouselsById({ ad_account_id: accountId, carousels: ids.join(',') })) || []
    return new Map(carousels.map((item) => [String(item.id), item]))
  } catch (e) {
    return new Map()
  }
}

async function rebuildMaterialLedger(materialInfo, accountId) {
  const [internalMaterialMap, carouselMap] = await Promise.all([
    queryInternalMaterialMap(materialInfo),
    queryCarouselMap(materialInfo, accountId)
  ])

  const image = (materialInfo.image_material_list || []).map((raw, index) => {
    const detail = internalMaterialMap.get(String(raw.inner_id))
    return detail
      ? attachExistingMaterial(dataFormatter.image(detail), MATERIAL_TYPE.IMAGE, raw, index)
      : makeFallbackMaterial(MATERIAL_TYPE.IMAGE, raw, index)
  })

  const video = (materialInfo.video_material_list || []).map((raw, index) => {
    const detail = internalMaterialMap.get(String(raw.inner_id))
    return detail
      ? attachExistingMaterial(dataFormatter.video(detail), MATERIAL_TYPE.VIDEO, raw, index)
      : makeFallbackMaterial(MATERIAL_TYPE.VIDEO, raw, index)
  })

  const imageAudio = (materialInfo.carousel_material_list || []).map((raw, index) => {
    const detail = carouselMap.get(String(raw.carousel_id))
    if (!detail) return makeFallbackMaterial(MATERIAL_TYPE.IMAGE_AUDIO, raw, index)

    const firstImage = detail.images?.[0] || {}
    return attachExistingMaterial(
      {
        ...detail,
        name: detail.file_name || `图文素材（${String(raw.carousel_id)}）`,
        width: firstImage.width || 0,
        height: firstImage.height || 0,
        url: firstImage.url || '',
        preview_url: firstImage.url || ''
      },
      MATERIAL_TYPE.IMAGE_AUDIO,
      raw,
      index
    )
  })

  const title = (materialInfo.title_material_list || []).map((raw) => ({
    ...clone(raw),
    _stdExisting: true,
    _stdRaw: clone(raw)
  }))

  return { image, video, imageAudio, title }
}

function makeKeywordsDesc(keywords) {
  if (!keywords?.length) return '未添加关键词'
  const firstKeyword = typeof keywords[0] === 'string' ? keywords[0] : keywords[0].word || keywords[0].keyword || ''
  return `已选${firstKeyword}...共${keywords.length}个关键词`
}

function rebuildStdTargetingForm(audience, accountId) {
  const source = audience && typeof audience === 'object' ? audience : {}
  const targetingForm = {
    ...getInitForm({
      defaultAccount: { id: accountId },
      adMarketingScene: TOUTIAO_AD_MARKETING_SCENE.VERSION_3
    }),
    ...withoutUndefined(
      Object.entries(source).reduce((result, [key, value]) => {
        if (value !== null) result[key] = clone(value)
        return result
      }, {})
    ),
    account: { id: accountId },
    ad_marketing_scene: TOUTIAO_AD_MARKETING_SCENE.VERSION_3
  }

  if (source.platform?.length) targetingForm.platform = source.platform[0]
  else targetingForm.platform = TARGETING_PLATFORM.UNLIMITED

  if (source.district === DISTRICT_TYPE.REGION) {
    targetingForm.regionType = REGION_TYPE.REGION
    targetingForm.region = source.city || []
  }

  targetingForm.audiences = {
    [accountId]: {
      accountId,
      includes: source.retargeting_tags_include || [],
      excludes: source.retargeting_tags_exclude || []
    }
  }
  if (source.retargeting_tags_include?.length || source.retargeting_tags_exclude?.length) {
    targetingForm.audienceType = AUDIENCE_TYPE.AUDIENCE
  }

  if (hasOwn(source, 'smart_extend')) targetingForm.auto_extend_enabled = source.smart_extend
  if (source.auto_extend_targets) targetingForm.autotend = source.auto_extend_targets

  if (source.device_brand?.length) {
    targetingForm.brand_type = 'CUSTOM'
    targetingForm.brand = source.device_brand
  }
  if (source.launch_price) {
    targetingForm.price_type = 'CUSTOM'
    targetingForm.launch_price = source.launch_price
  }

  if (source.interest_action_mode === INTEREST_ACTION_TYPES.CUSTOM) {
    targetingForm.action_tags = [
      ...(source.action_categories || []).map((id) => ({ id, type: 'CATEGORY' })),
      ...(source.action_words || []).map((id) => ({ id, type: 'KEYWORD' }))
    ]
    targetingForm.word_tags = [
      ...(source.interest_categories || []).map((id) => ({ id, type: 'CATEGORY' })),
      ...(source.interest_words || []).map((id) => ({ id, type: 'KEYWORD' }))
    ]
  }

  return targetingForm
}

export async function rebuildStdProjectForm(detail, { account, accountId }) {
  const planInfo = detail.plan_info || {}
  const outPlanInfo = detail.out_plan_info || {}
  const materialInfo = getMaterialInfo(detail)
  const materialLedger = await rebuildMaterialLedger(materialInfo, accountId)
  const configForm = getConfigFormModel({ isV3: true })
  const projectDefaults = getProjectFormModel({ isV3: true })
  const planDefaults = getPlanFormModel()
  const creativeDefaults = getCreativeFormModel()
  const audience = outPlanInfo.audience || {}
  const isUnlimitedAudience = Object.keys(audience).length === 0
  const scheduleType = outPlanInfo.schedule_type || projectDefaults.delivery_setting.schedule_type
  const scheduleTime = outPlanInfo.schedule_time || DEFAULT_SCHEDULE_TIME
  const keywords = outPlanInfo.keywords || []
  const brandInfo = outPlanInfo.brand_info || {}

  return {
    ...configForm,
    isV3: true,
    ad_account_id: accountId,
    accounts: account ? [account] : [{ id: accountId }],
    editContext: {
      internalPlanId: planInfo.plan_id,
      internalPlanName: planInfo.plan_name,
      gameId: planInfo.game_id,
      gameName: planInfo.game_name,
      callbackSwitch: planInfo.callback_switch,
      trackChainAddr: planInfo.track_chain_addr,
      projectId: outPlanInfo.project_id,
      startTime: outPlanInfo.start_time,
      landingType: outPlanInfo.landing_type,
      marketingGoal: outPlanInfo.marketing_goal,
      deliveryMode: outPlanInfo.delivery_mode,
      adType: outPlanInfo.ad_type
    },
    project: {
      ...projectDefaults,
      project_name: outPlanInfo.name || '',
      plan_name: planInfo.plan_name || outPlanInfo.name || '',
      ad_account_id: accountId,
      ch_platform_id: planInfo.ch_platform_id || projectDefaults.ch_platform_id,
      game: { id: planInfo.game_id },
      callback_switch: planInfo.callback_switch ?? projectDefaults.callback_switch,
      landing_type: outPlanInfo.landing_type || projectDefaults.landing_type,
      micro_promotion_type: outPlanInfo.delivery_medium || projectDefaults.micro_promotion_type,
      micro_app_instance_id: outPlanInfo.instance_id || '',
      ad_type: outPlanInfo.ad_type || projectDefaults.ad_type,
      delivery_mode: outPlanInfo.delivery_mode || projectDefaults.delivery_mode,
      delivery_type: outPlanInfo.delivery_type || projectDefaults.delivery_type,
      marketing_goal: outPlanInfo.marketing_goal || projectDefaults.marketing_goal,
      audience_type: isUnlimitedAudience ? PROJECT_AUDIENCE_TYPE.UNLIMITED : PROJECT_AUDIENCE_TYPE.CUSTOM,
      keywordSwitch: keywords.length ? 1 : 0,
      keywords,
      keywordsDesc: makeKeywordsDesc(keywords),
      delivery_setting: {
        ...projectDefaults.delivery_setting,
        budget: outPlanInfo.budget ?? '',
        budget_mode: outPlanInfo.budget_mode || BUDGET_MODE.DAY,
        schedule_type: scheduleType,
        schedule_time: scheduleTime,
        schedule_time_type: scheduleTime === DEFAULT_SCHEDULE_TIME ? SCHEDULE_TIME_TYPE.ALL : SCHEDULE_TIME_TYPE.RANGE,
        date_range: [outPlanInfo.start_time || '', outPlanInfo.end_time || ''],
        bid_type: outPlanInfo.bid_type || projectDefaults.delivery_setting.bid_type,
        pricing: outPlanInfo.pricing || projectDefaults.delivery_setting.pricing,
        cpa_bid: outPlanInfo.cpa_bid ?? '',
        first_roi_goal: outPlanInfo.first_roi_goal ?? '',
        roi_goal: outPlanInfo.roi_goal ?? '',
        bid_value_type: BID_VALUE_TYPE.FIXED,
        roi_bid_value_type: BID_VALUE_TYPE.FIXED,
        optimize_goal: {
          ...projectDefaults.delivery_setting.optimize_goal,
          external_action: outPlanInfo.external_action || projectDefaults.delivery_setting.optimize_goal.external_action,
          deep_external_action:
            outPlanInfo.deep_external_action || projectDefaults.delivery_setting.optimize_goal.deep_external_action,
          deep_bid_type: outPlanInfo.deep_bid_type || projectDefaults.delivery_setting.optimize_goal.deep_bid_type
        }
      }
    },
    plan: {
      ...planDefaults,
      plan_name: outPlanInfo.name || '',
      materials_type: MATERIAL_STYLE.PROMOTION_MATERIALS,
      aigc_dynamic_creative_switch: outPlanInfo.aigc_dynamic_creative_switch || planDefaults.aigc_dynamic_creative_switch,
      is_comment_disable: outPlanInfo.is_comment_disable || planDefaults.is_comment_disable,
      title_material_list: materialLedger.title,
      yuntu_category_id: brandInfo.yuntu_category_id ? [String(brandInfo.yuntu_category_id)] : [],
      cdp_brand_id: brandInfo.cdp_brand_id || '',
      cdp_brand_text: brandInfo.cdp_brand_name || '',
      brand_name_id: brandInfo.brand_name_id || ''
    },
    targetings: [
      {
        targetingParamJson: rebuildStdTargetingForm(audience, accountId),
        accountIds: accountId
      }
    ],
    creatives: [
      {
        ...creativeDefaults,
        creative_name: outPlanInfo.name || '',
        materialType: MATERIAL_TYPE_ID[MATERIAL_TYPE.VIDEO],
        selectedMaterials: {
          video: materialLedger.video,
          image: materialLedger.image,
          imageAudio: materialLedger.imageAudio
        }
      }
    ]
  }
}

function getMaterialInfo(detail) {
  const materialInfo = detail?.material_info
  const invalidKey = MATERIAL_LIST_KEYS.find((key) => !Array.isArray(materialInfo?.[key]))
  if (invalidKey) throw new Error(`项目素材数据结构异常：${invalidKey} 应为数组`)
  return materialInfo
}

function buildAudience(form, rawAudience) {
  if (form.project.audience_type === PROJECT_AUDIENCE_TYPE.UNLIMITED) return {}

  const targetingForm = form.targetings?.[0]?.targetingParamJson || {}
  const normalized = getSubmitTargetingParams(targetingForm, { isV3: true })
  const { audiences, ...normalizedAudience } = normalized
  const retargeting = audiences
    ? {
      retargeting_tags_include: audiences.retargeting_tags_include,
      retargeting_tags_exclude: audiences.retargeting_tags_exclude
    }
    : {}
  const unmanaged = Object.entries(rawAudience || {}).reduce((result, [key, value]) => {
    if (!TARGETING_MANAGED_KEYS.includes(key) && value !== null) result[key] = clone(value)
    return result
  }, {})
  return withoutUndefined({ ...unmanaged, ...normalizedAudience, ...retargeting })
}

function buildProjectParams(form, rawOutPlanInfo) {
  const project = form.project
  const plan = form.plan
  const deliverySetting = project.delivery_setting
  const brandCategory = plan.yuntu_category_id || []
  const projectParams = {
    name: project.project_name?.trim(),
    delivery_type: rawOutPlanInfo.delivery_type || DELIVERY_TYPE.NORMAL,
    budget: toEditableNumber(deliverySetting.budget),
    cpa_bid: toEditableNumber(deliverySetting.cpa_bid),
    roi_goal: toEditableNumber(deliverySetting.roi_goal),
    first_roi_goal: toEditableNumber(deliverySetting.first_roi_goal),
    schedule_type: deliverySetting.schedule_type,
    schedule_time:
      deliverySetting.schedule_time_type === SCHEDULE_TIME_TYPE.ALL ? DEFAULT_SCHEDULE_TIME : deliverySetting.schedule_time,
    audience: buildAudience(form, rawOutPlanInfo.audience),
    aigc_dynamic_creative_switch: plan.aigc_dynamic_creative_switch,
    is_comment_disable: plan.is_comment_disable,
    brand_info: {
      yuntu_category_id: brandCategory.length ? brandCategory[brandCategory.length - 1] : '',
      cdp_brand_id: plan.cdp_brand_id || '',
      brand_name_id: plan.brand_name_id || ''
    }
  }

  if (deliverySetting.schedule_type === SCHEDULE_TYPE.START_END) {
    projectParams.end_time = deliverySetting.date_range?.[1]
  }
  if (project.ad_type === 'SEARCH') projectParams.keywords = project.keywords || []

  ;['deep_cpabid', 'seven_roi_goal', 'auto_extend_traffic'].forEach((key) => {
    if (hasOwn(rawOutPlanInfo, key)) projectParams[key] = rawOutPlanInfo[key]
  })

  return withoutUndefined(projectParams)
}

function getExistingMaterialPayload(item) {
  const raw = clone(item._stdRaw || {})
  MATERIAL_STATUS_KEYS.forEach((key) => delete raw[key])
  delete raw.inner_id
  return raw
}

function getExistingImageVideoPayload(item) {
  const raw = getExistingMaterialPayload(item)
  if (raw.material_id !== null && typeof raw.material_id !== 'undefined') {
    raw.material_id = String(raw.material_id)
  }
  return raw
}

function buildMaterialPayload(form) {
  const selectedMaterials = form.creatives?.[0]?.selectedMaterials || {}
  const materials = []

  const imageMaterialList = (selectedMaterials.image || []).map((item) => {
    if (item._stdExisting) return getExistingImageVideoPayload(item)
    const imageMode = getCreativeImageMode(item)
    const imageId = imageMode.images?.[0]?.image_id
    if (!imageMode.image_mode || !imageId) throw new Error(`图片素材“${item.name || item.id}”尺寸不符合要求`)
    materials.push({ key: imageId, type: 'IMAGE', inner_id: String(item.id) })
    return { image_mode: imageMode.image_mode, image_id: imageId }
  })

  const videoMaterialList = (selectedMaterials.video || []).map((item) => {
    if (item._stdExisting) return getExistingImageVideoPayload(item)
    const imageMode = getCreativeImageMode(item)
    if (!imageMode.image_mode || !imageMode.video_id || !imageMode.video_cover_id) {
      throw new Error(`视频素材“${item.name || item.id}”或其封面不符合要求`)
    }
    materials.push({ key: imageMode.video_id, type: 'VIDEO', inner_id: String(item.id) })
    materials.push({
      key: imageMode.video_cover_id,
      type: 'IMAGE',
      inner_id: String(item.cover_id),
      defaultCover: !item.cover_replaced_type || item.cover_replaced_type === MATERIAL_TYPE.POSTER
    })
    return imageMode
  })

  const carouselMaterialList = (selectedMaterials.imageAudio || []).map((item) => {
    if (item._stdExisting) return getExistingMaterialPayload(item)
    if (!item.id) throw new Error('图文素材缺少 carousel_id')
    return { carousel_id: item.id }
  })

  const titleMaterialList = (form.plan.title_material_list || []).map((item) => {
    const existingTitleChanged = item._stdExisting && item.title !== item._stdRaw?.title
    if (item._stdExisting && !existingTitleChanged) return pick(getExistingMaterialPayload(item), ['material_id', 'title'])
    return { title: item.title?.trim() }
  })

  return {
    video_material_list: videoMaterialList,
    image_material_list: imageMaterialList,
    carousel_material_list: carouselMaterialList,
    title_material_list: titleMaterialList,
    materials
  }
}

export function getMaterialCounts(form) {
  const selected = form.creatives?.[0]?.selectedMaterials || {}
  const materialItems = [...(selected.video || []), ...(selected.image || []), ...(selected.imageAudio || [])]
  return {
    materialTotal: materialItems.length,
    titleTotal: form.plan?.title_material_list?.length || 0,
    newMaterialTotal: materialItems.filter((item) => !item._stdExisting).length
  }
}

export function validateStdProjectBeforeSubmit(form, { originalMaterialTotal }) {
  if (!form.project.project_name?.trim()) throw new Error('请输入项目名称')

  const { materialTotal, titleTotal, newMaterialTotal } = getMaterialCounts(form)
  if (titleTotal > 10) throw new Error('创意标题最多10个')
  if (form.plan.title_material_list.some((item) => !item.title?.trim())) throw new Error('创意标题不能为空')
  if (newMaterialTotal > 200) throw new Error(`单次最多新增200个图片、视频和图文素材，当前新增${newMaterialTotal}个`)

  if (originalMaterialTotal <= 800 && materialTotal > 800) {
    throw new Error(`图片、视频、图文素材合计最多800个，当前${materialTotal}个`)
  }
  if (originalMaterialTotal > 800 && newMaterialTotal > 0) {
    throw new Error('原项目素材已超过800个，请先提交素材移除并重新加载后再添加')
  }
}

export function buildStdProjectUpdateRequest({ accountId, projectId, form, rawDetail }) {
  return {
    accountId,
    project_id: projectId,
    project_params: buildProjectParams(form, rawDetail.out_plan_info || {}),
    materials: buildMaterialPayload(form)
  }
}

export function getOriginalMaterialTotal(detail) {
  const materialInfo = getMaterialInfo(detail)
  return ['video_material_list', 'image_material_list', 'carousel_material_list'].reduce(
    (total, key) => total + (materialInfo[key]?.length || 0),
    0
  )
}
